// Blob colony-identity decode (match-screen design D-match-3, tasks.md 3.2): a pure
// client mirror of `waggle-proto::lod::unpack_blob_id` — never a server dependency,
// never a `waggle-proto`/Rust change (D1). Golden-bound to the committed Rust-emitted
// `client/src/canvas/__fixtures__/blob-id-vectors.json` (see `blob-colony.test.ts`),
// not verified only against a TS self-round-trip (A2).

/** The rounds viewer's fixed low-detail grid, shared by live streaming. */
export const LOD_CELLS_PER_AXIS = 8;

/** Mirrors `waggle_proto::lod::supercells_per_axis`; field size no longer changes identity. */
export function supercellsPerAxis(_fieldSize: number): number {
  return LOD_CELLS_PER_AXIS;
}

export interface UnpackedBlobId {
  colonyId: number;
  sx: number;
  sy: number;
}

/**
 * The exact inverse of `waggle-proto::lod::pack_blob_id`: `blob_id = colony_id *
 * supercells_per_axis² + (sy * supercells_per_axis + sx)`. Pure integer arithmetic —
 * both modes use the same fixed grid, so this recovers whichever producer packed the id.
 */
export function unpackBlobId(blobId: number, fieldSize: number): UnpackedBlobId {
  const spa = supercellsPerAxis(fieldSize);
  const cellsSq = spa * spa;
  const colonyId = Math.floor(blobId / cellsSq);
  const supercellIndex = blobId % cellsSq;
  return {
    colonyId,
    sx: supercellIndex % spa,
    sy: Math.floor(supercellIndex / spa),
  };
}

/**
 * Decodes a blob's colony id, validated against the match roster
 * (`manifest.colonyIds`). A decode that lands outside the roster returns `null`
 * rather than aliasing another colony's colour (client-match: "a blob decoding to an
 * out-of-range colony is skipped, never aliased").
 */
export function decodeBlobColony(
  blobId: number,
  fieldSize: number,
  colonyIds: readonly number[],
): number | null {
  const { colonyId } = unpackBlobId(blobId, fieldSize);
  return colonyIds.includes(colonyId) ? colonyId : null;
}
